
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

export default function Services() {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const services = [
    { icon: 'ri-chat-smile-3-line', title: t('services.service1'), description: t('services.service1Desc'), num: '01' },
    { icon: 'ri-ruler-line', title: t('services.service2'), description: t('services.service2Desc'), num: '02' },
    { icon: 'ri-palette-line', title: t('services.service3'), description: t('services.service3Desc'), num: '03' },
    { icon: 'ri-tools-line', title: t('services.service4'), description: t('services.service4Desc'), num: '04' },
    { icon: 'ri-shield-check-line', title: t('services.service5'), description: t('services.service5Desc'), num: '05' }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-stone-800">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-amber-300 text-xs font-semibold tracking-[0.2em] mb-4 uppercase">{t('services.badge')}</p>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
            {t('services.title')}
          </h2>
          <div className="w-12 h-px bg-amber-400 mx-auto mb-6"></div>
          <p className="text-stone-400 text-sm max-w-2xl mx-auto leading-relaxed font-light">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {services.map((service, index) => (
            <div 
              key={index}
              className={`group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-7 hover:bg-white/10 hover:border-amber-400/30 transition-all duration-500 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 flex items-center justify-center bg-amber-400/10 rounded-lg group-hover:bg-amber-400/20 transition-colors">
                  <i className={`${service.icon} text-amber-300 text-xl`}></i>
                </div>
                <span className="text-white/10 text-2xl font-light group-hover:text-white/20 transition-colors">{service.num}</span>
              </div>
              <h3 className="text-base font-medium text-white mb-2">{service.title}</h3>
              <p className="text-stone-500 text-xs leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className={`flex flex-col md:flex-row gap-0 rounded-2xl overflow-hidden transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Left Side - Visual */}
          <div className="md:w-1/2 relative min-h-[480px]">
            <img 
              src="https://public.readdy.ai/ai/img_res/47af4094fb08854fbab535e132ba3529.jpg"
              alt="Professional Service"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-stone-900/20"></div>
          </div>

          {/* Right Side - Info */}
          <div className="md:w-1/2 bg-amber-700 flex flex-col items-center justify-center p-12 md:p-16">
            <div className="text-center">
              <p className="text-amber-200 text-xs tracking-[0.2em] uppercase mb-6">{t('services.ctaBadge')}</p>
              <h3 className="text-3xl font-light text-white mb-3 leading-snug">
                {t('services.ctaHeading1')}
              </h3>
              <h3 className="text-3xl font-light text-white mb-8 leading-snug">
                {t('services.ctaHeading2')}
              </h3>
              
              <p className="text-amber-100/80 text-sm leading-relaxed mb-10 max-w-sm mx-auto font-light">
                {t('services.ctaDesc')}
              </p>
              
              <a 
                href="#contact"
                className="inline-flex items-center gap-3 bg-white text-stone-800 px-10 py-3.5 rounded-full text-sm font-medium hover:bg-amber-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('services.ctaButton')}
                <i className="ri-arrow-right-line text-base"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
